import { isEmpty } from "lodash";

export function commaAndFormatStringList(texts: string[]) {
  if (texts.length == 0) {
    return "";
  }
  if (texts.length == 1) {
    return texts[0];
  }
  const last = texts.pop();
  return texts.join(", ") + " and " + last;
}

export function toLocaleString(amount: number = 0): string {
  return amount.toLocaleString();
}
export function userFormatCurrency(amount: number = 0): string {
  return Number(amount).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 4,
  });
}

/**
 * A charged amount, always to the cent.
 *
 * The four decimals `userFormatCurrency` allows are what a per-proxy or per-GB
 * rate needs and what a total must never show, $18.8367 is not a sum anyone is
 * billed. Two formatters because there are two kinds of figure, not because one
 * of them drifted.
 */
export function formatUsd(amount: number): string {
  return amount.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * Apply discount multiplier to a price.
 * Discount is a multiplier (0.5 = pay 50% of the price).
 * Returns original price if no discount provided.
 */
export function getEffectivePrice(price: number, discount?: number): number {
  if (discount) {
    return price * discount;
  }
  return price;
}

/**
 * Converts a byte count into a GB string:
 *  - 0 bytes        → "0 GB"
 *  - <10 MB         → "<0.01 GB"
 *  - <1 GB          → two decimal places (e.g. "0.12 GB")
 *  - 1 GB–<100 GB   → one decimal place  (e.g. "42.7 GB")
 *  - ≥100 GB        → no decimal places  (e.g. "128 GB")
 */
export function formatBytesToGB(bytes: number): string {
  const BYTES_IN_MB = 1024 ** 2;
  const BYTES_IN_GB = 1024 ** 3;

  if (bytes === 0) {
    return "0 GB";
  }

  // Less than 10 MB: <0.01 GB
  if (bytes < 10 * BYTES_IN_MB) {
    return "<0.01 GB";
  }

  const gb = bytes / BYTES_IN_GB;

  if (gb < 1) {
    // Less than 1 GB: Two decimal places.
    return `${gb.toFixed(2)} GB`;
  } else if (gb < 100) {
    // 1 GB to 100 GB: One decimal place.
    return `${gb.toFixed(1)} GB`;
  } else {
    // Over 100 GB: No decimal places.
    return `${Math.round(gb)} GB`;
  }
}

export function userReadableBytes(bytes: number, decimals = 2) {
  if (!+bytes) return "0 Bytes";

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
}

//-{country_code}-{N} part. We should only lowercase the {country_code} part.
//Lower case only countryCodes.
export function lowerCaseSpecial(text: string) {
  if (typeof text === "string") {
    const arr = text.split(/-(.*)/s);
    if (arr.length > 1) {
      return `${arr[0]}-${arr[1].toLowerCase()}`;
    }
  }

  return text;
}

export function capitalizeFirstLetter(string: string) {
  if (string.charAt(0)) {
    return string.charAt(0).toUpperCase() + string.slice(1);
  }
  return string;
}

export function capitalizeFirstLetterOfEachWord(string: string) {
  return string.replace(/(?:^\w|[A-Z]|\b\w)/g, function (word: string) {
    return word.toUpperCase();
  });
}

export function humanize(str: string) {
  var i,
    frags = str.split("_");
  for (i = 0; i < frags.length; i++) {
    frags[i] = frags[i].charAt(0).toUpperCase() + frags[i].slice(1);
  }
  return frags.join(" ");
}

export function getQueryParamValue(value: string | null | undefined) {
  if (isEmpty(value)) {
    return undefined;
  } else {
    return value;
  }
}

export function joinWithCommaEndWithAnd(parts: string[]) {
  return parts.reduce(
    (acc: string, part: string, index: number, array: string[]) => {
      const isLast = index === array.length - 1;
      const isFirst = index === 0;
      if (isFirst) {
        return part;
      }
      if (isLast) {
        return `${acc} and ${part}`;
      }
      return `${acc}, ${part}`;
    },
    ""
  );
}

type Split<S extends string, D extends string> = string extends S
  ? string[]
  : S extends ""
    ? []
    : S extends `${infer T}${D}${infer U}`
      ? [T, ...Split<U, D>]
      : [S];

export function typedSplit<T extends string, S extends string>(str: T, sep: S) {
  return str.split(sep) as Split<T, S>;
}
